import { createFileRoute } from "@tanstack/react-router";
import { HelpLink, HelpNote, HelpPage, HelpSection } from "@/components/HelpPage";

/**
 * Mesures indicatives par taille.
 * Les valeurs restent volontairement vides (« — ») tant que les mesures
 * exactes ne sont pas fournies : il suffit de remplacer les tirets ici.
 */
const sizeRows: Array<{ size: string; bust: string; waist: string; hips: string }> = [
  { size: "S", bust: "—", waist: "—", hips: "—" },
  { size: "M", bust: "—", waist: "—", hips: "—" },
  { size: "L", bust: "—", waist: "—", hips: "—" },
  { size: "XL", bust: "—", waist: "—", hips: "—" },
];

export const Route = createFileRoute("/guide-des-tailles")({
  head: () => ({
    meta: [
      { title: "Guide des tailles — Sunset Concept Store" },
      {
        name: "description",
        content:
          "Guide des tailles Sunset Concept Store : repères poitrine, taille et hanches pour choisir la taille qui vous convient.",
      },
      { property: "og:title", content: "Guide des tailles — Sunset Concept Store" },
      {
        property: "og:description",
        content: "Trouvez plus facilement la taille qui vous convient grâce au guide Sunset Concept Store.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sunsetconcept.store/guide-des-tailles" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://sunsetconcept.store/guide-des-tailles" }],
  }),
  component: GuideDesTaillesPage,
});

function GuideDesTaillesPage() {
  return (
    <HelpPage title="Guide des tailles" intro="Trouvez plus facilement la taille qui vous convient grâce à notre guide.">
      <section>
        <div className="-mx-6 overflow-x-auto px-6 sm:mx-0 sm:px-0">
          <table className="w-full min-w-[30rem] border-collapse text-left">
            <caption className="sr-only">Correspondance des tailles Sunset Concept Store</caption>
            <thead>
              <tr className="border-b border-foreground/20">
                {["Taille", "Poitrine", "Taille", "Hanches"].map((h, i) => (
                  <th
                    key={`${h}-${i}`}
                    scope="col"
                    className="label-xs pb-4 pr-4 text-[0.55rem] font-normal text-foreground last:pr-0"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sizeRows.map((row) => (
                <tr key={row.size} className="border-b border-border">
                  <th scope="row" className="py-5 pr-4 font-serif text-lg font-light tracking-tight">
                    {row.size}
                  </th>
                  <td className="py-5 pr-4 text-sm tabular-nums text-muted-foreground">{row.bust}</td>
                  <td className="py-5 pr-4 text-sm tabular-nums text-muted-foreground">{row.waist}</td>
                  <td className="py-5 text-sm tabular-nums text-muted-foreground">{row.hips}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <HelpSection title="Conseil SUNSET">
        <p>
          Les coupes peuvent varier légèrement selon les modèles et les matières. Consultez également les informations
          présentes sur chaque fiche produit avant de choisir votre taille.
        </p>
      </HelpSection>

      <HelpSection title="Besoin d'aide ?">
        <p>
          Si vous hésitez entre deux tailles, <HelpLink to="/contact">contactez-nous</HelpLink> avant de commander et
          notre équipe pourra vous orienter.
        </p>
      </HelpSection>

      <HelpNote>
        Retrouvez les réponses aux questions les plus fréquentes dans notre <HelpLink to="/faq">FAQ</HelpLink>.
      </HelpNote>
    </HelpPage>
  );
}
